import {
  Button,
  CardMedia,
  CircularProgress,
  ClickAwayListener,
  Container,
  Grid,
  Input,
  styled,
  Tab,
  Tabs,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import {
  GridColDef,
  GridFilterItem,
  GridCellParams,
  GridFilterInputValue,
  GridFilterOperator,
} from '@mui/x-data-grid';
import React, { useContext, useEffect, useState } from 'react';

import UpdateOffice from 'components/Report/UpdateOffice';
import { toast } from 'react-toastify';
import { getPatientsByOfficeId, uploadMdacProviderData } from 'services/profileServices';
import { useAppDispatch } from 'store/hooks';
import { getOfficeListByStatus } from 'store/slices/secureMessageSlice';
import COLORS from 'theme/colors';
import { StyledDataGrid } from 'theme/datagrid';
import { AuthContext } from 'utils/AuthContext';
import { CustomToolbar } from 'utils/customToolBar';
import { OrganizationTypeFilter, RegionFilter } from 'utils/gridOperators';
import { normalizePhone, formatPhoneNumber } from 'utils/dataFormatter';

import chat from 'assets/images/tasklist.svg';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import CustomDataGrid from 'utils/customDataGrid';
import { validateSession } from 'services/validateSession';

const OfficeList = () => {
  const pageKey = 'Office_list';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, setUserDetails } = useContext(AuthContext);
  const [editItem, setEditItem] = useState<any>(null);
  const [addOrUpdateOffice, setAddOrUpdateOffice] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [officeList, setOfficeList] = useState<any[] | null>();
  const [loading, setLoading] = useState<boolean>(true);
  const [officeCount, setOfficeCount] = useState<any>();
  const [patientDetails, setPatientDetails] = useState<any>(null);
  const [open, setOpen] = React.useState<any>();

  const phoneNumberFilterOperators: GridFilterOperator[] = [
    {
      label: 'contains',
      value: 'contains',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.value) return null;
        const filterValue = normalizePhone(filterItem.value);

        return (params: GridCellParams) => {
          const normalizedCell = normalizePhone(params.value);
          return normalizedCell.includes(filterValue);
        };
      },
      InputComponent: GridFilterInputValue,
    },
  ];

  const handleTooltipClose = () => {
    setOpen(null);
    setPatientDetails(null);
  };
  const handleTooltipOpen = (e: any, id: number) => {
    e.stopPropagation();
    setOpen(id);
    setPatientDetails(null);
    dispatch(getPatientsByOfficeId(id))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setPatientDetails(response.data);
        }
      });
  };

  const moveToPatientChart = async (patientId: any) => {
    await validateSession();
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
    const data = {
      ...user,
      patientId,
    };
    setUserDetails(data);
  };

  useEffect(() => {
    if (open !== undefined) {
      console.log('Updated open:', open);
    }
  }, [open]);

  const StyledTooltip = styled(({ className, color, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltipArrow}`]: {
      backgroundColor: '#fff!important',
      boxShadow: theme.shadows[1],
    },
    [`& .${tooltipClasses.arrow}`]: {
      '&:before': {
        border: '1px solid #fff',
        color: '#fff',
      },
    },
    [`& .${tooltipClasses.tooltip}`]: {
      border: '1px solid #fff',
      padding: '12px',
      borderRadius: '8px',
    },
  }));

  const listOffices = async () => {
    setLoading(true);
    const data = {
      id: user?.userId,
      listType: selectedTab === 0 ? 'Approved' : selectedTab === 1 ? 'In-Pipeline' : 'Rejected',
    };

    await dispatch(getOfficeListByStatus(data.listType))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setOfficeList(response?.data?.officeList);
          setLoading(false);
          setOfficeCount(response?.data);
        }
      });
  };

  useEffect(() => {
    listOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, selectedTab]);

  const columns: GridColDef[] = React.useMemo(() => {
    const baseCols: GridColDef[] = [
      {
        field: 'id',
        headerName: 'Company Id',
        hideable: false,
        width: 100,
        valueFormatter: (params: any) => {
          return 'CP_' + params.id;
        },
      },
      {
        field: 'officeName',
        headerName: 'Practice/Organization Name',
        width: 280,
        hideable: false,
      },
      {
        field: 'officeType',
        headerName: 'Type',
        width: 130,
        hideable: false,
        filterOperators: OrganizationTypeFilter,
      },
      {
        field: 'officePhoneNumber',
        headerName: 'Phone Number',
        width: 130,
        type: 'phone',
        hideable: false,
        renderCell: (params: any) => formatPhoneNumber(params?.row?.officePhoneNumber),
        filterOperators: phoneNumberFilterOperators,
      },
      {
        field: 'officeFaxNumber',
        headerName: 'Fax Number',
        width: 130,
        type: 'phone',
        renderCell: (params: any) => formatPhoneNumber(params?.row?.officeFaxNumber),
      },
      {
        field: 'businessEmail',
        headerName: 'Business Email',
        width: 200,
      },
      {
        field: 'region',
        headerName: 'Region',
        width: 128,
        hideable: false,
        filterOperators: RegionFilter,
      },
      {
        field: 'address',
        headerName: 'Street Address',
        width: 250,
        hideable: false,
      },
      {
        field: 'city',
        headerName: 'City',
        width: 100,
        hideable: false,
      },
      {
        field: 'state',
        headerName: 'State',
        width: 120,
        hideable: false,
      },
      {
        field: 'zipcode',
        headerName: 'ZIP Code',
        width: 80,
        hideable: false,
      },
      {
        field: 'websiteUrl',
        headerName: 'Website URL',
        width: 200,
      },
    ];

    // Add "Associated Patients" column only for In-Pipeline tab
    if (selectedTab === 1) {
      baseCols.unshift({
        field: 'action',
        headerName: 'Associated Patients',
        filterable: false,
        sortable: false,
        hideable: false,
        renderCell: (params: any) => (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <StyledTooltip
              arrow
              onClose={handleTooltipClose}
              onMouseLeave={handleTooltipClose}
              open={open === params?.id}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={
                <span style={{ height: '150px' }}>
                  {patientDetails === null ? (
                    <CircularProgress />
                  ) : patientDetails?.length !== 0 ? (
                    <Grid container spacing={1}>
                      {patientDetails?.map((g: any) => (
                        <Grid item xs={12} key={g.id}>
                          <Typography
                            sx={{
                              color: '#3467FF',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer',
                            }}
                            onClick={() => moveToPatientChart(g.id)}
                          >
                            {g.firstName} {g.lastName}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography>No Patients Associated</Typography>
                  )}
                </span>
              }
              placement='right-end'
              sx={{ paddingLeft: '50px' }}
              PopperProps={{
                disablePortal: true,
                modifiers: [{ name: 'offset', options: { offset: [20, -60] } }],
              }}
            >
              <CardMedia
                src={chat}
                component='img'
                sx={{ width: '24px', height: 'auto' }}
                onClick={(e: any) => handleTooltipOpen(e, params?.row?.id)}
              />
            </StyledTooltip>
          </ClickAwayListener>
        ),
      });
    }

    return baseCols;
  }, [
    selectedTab,
    open,
    patientDetails,
    phoneNumberFilterOperators,
    StyledTooltip,
    handleTooltipOpen,
    moveToPatientChart,
  ]);

  //Default filter for officeType
  const defaultFilterModel = {
    items: [
      {
        field: 'officeName',
        operator: 'contains',
        value: '',
      },
    ],
  };

  const rowSelect = (params: any) => {
    // eslint-disable-next-line array-callback-return
    const editItem = officeList?.filter((d: any) => {
      if (d.id === params.row.id) {
        return d;
      }
    });
    setEditItem(editItem);
    setAddOrUpdateOffice(true);
  };

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  const handleOpenAddOffice = () => {
    setAddOrUpdateOffice(true);
    setEditItem(null);
  };

  const handleCloseAddOffice = () => {
    setAddOrUpdateOffice(false);
    setEditItem(null);
    listOffices();
  };

  type UploadMdacProviderDataResult =
    | {
        success: boolean;
        message: any;
        hasFile?: undefined;
        file?: undefined;
        errorMsg?: string;
        errorCode?: string;
      }
    | {
        success: boolean;
        hasFile: boolean;
        file: Blob;
        message?: undefined;
        errorMsg?: string;
        errorCode?: string;
      };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const maxSizeInBytes = 100 * 1024; // ~100KB as an estimated upper size for 200 records

    if (!['xls', 'xlsx'].includes(fileExtension || '')) {
      toast('Only .xls or .xlsx files are allowed.', {
        type: 'error',
        icon: true,
      });
      return;
    }

    if (file.size > maxSizeInBytes) {
      toast('File seems too large. Limit is ~200 rows (~500KB estimated).', {
        type: 'error',
        icon: true,
      });
      return;
    }
    setLoading(true);
    const result: UploadMdacProviderDataResult = await uploadMdacProviderData(file);

    if (result.success) {
      toast(result.message, {
        type: 'success',
        icon: true,
      });
    } else if (result.hasFile) {
      // ⬇️ Trigger download of the error file
      const url = URL.createObjectURL(result.file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'error_output.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast('Some records have errors. Please fix and re-upload.', {
        type: 'error',
        icon: true,
      });
    } else if (result?.message) {
      toast(result.message, {
        type: 'error',
        icon: true,
      });
    }
    e.target.value = ''; // Reset file input
    setLoading(false);
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          REFPOD: Referral Provider Organization Database
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
        <Grid container sx={{ background: '#FFFBF1', justifyContent: 'space-between' }}>
          <Grid item xs={6.5} mb='10px'>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{
                background: '#F9FAFB',
                borderRadius: '23px',
                border: '1px solid #F2F4F7',
                marginTop: '10px',
                padding: '6px 14px',
                '.MuiTab-root.Mui-selected': {
                  background: '#9F84FF',
                  color: '#fff',
                  borderRadius: '23px',
                  fontWeight: 500,
                },
              }}
              TabIndicatorProps={{
                style: {
                  background: 'transparent',
                },
              }}
              centered
            >
              <Tab
                label={
                  <Grid>
                    Approved
                    <span
                      style={{
                        background: selectedTab === 0 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {officeCount ? officeCount?.approvedCount : 0}
                    </span>
                  </Grid>
                }
              />
              <Tab
                label={
                  <Grid>
                    In-Pipeline
                    <span
                      style={{
                        background: selectedTab === 1 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {officeCount ? officeCount?.inPipelineCount : 0}
                    </span>
                  </Grid>
                }
              />

              <Tab
                label={
                  <Grid>
                    Rejected
                    <span
                      style={{
                        background: selectedTab === 2 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {officeCount ? officeCount?.rejectedCount : 0}
                    </span>
                  </Grid>
                }
              />
            </Tabs>
          </Grid>
          <Grid item sx={{ display: 'flex', alignItems: 'right', gap: 2, mt: 1 }}>
            <Button variant='text' component='label' size='small'>
              Upload Organization List
              <input type='file' accept='.xls,.xlsx' hidden onChange={(e) => handleFileUpload(e)} />
            </Button>
            <Button variant='text' onClick={() => handleOpenAddOffice()}>
              Create Organization
            </Button>
          </Grid>
        </Grid>
        <CustomDataGrid
          rows={officeList ? officeList : []}
          columns={columns}
          dashBordKey={pageKey + '_' + selectedTab}
          onRowSelect={rowSelect}
          userId={user.userId}
          defaultFilter={defaultFilterModel}
          setting={{ disableExport: !user?.permissions.includes('EXPORT_DATA') }}
        />
      </Grid>
      {addOrUpdateOffice === true ? (
        <UpdateOffice
          onClose={() => {
            handleCloseAddOffice();
          }}
          editData={editItem !== null ? editItem : null}
        />
      ) : null}
    </Container>
  );
};
export default OfficeList;
